import React, { useMemo } from 'react';
import RemoveIcon from '@mui/icons-material/Remove';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
    CATEGORY_FIELDS,
    SERIES_FIELDS,
    VALUE_FIELDS,
    getFirstValue,
    parseNumber
} from './chartDataUtils';

const VALUE_CANDIDATE_FIELDS = Array.from(new Set([
    ...VALUE_FIELDS,
    'nb',
    'number',
    'metric',
    'indicator',
    'indic',
    'currentValue',
    'current_value',
    'location',
    'locations',
    'rentals',
    'nb_location',
    'nb_locations',
    'nbLocation',
    'nbLocations'
]));

const METRIC_LABEL_FIELDS = [
    'label',
    'libelle',
    'name',
    'title',
    'metric_label',
    'metricLabel',
    'description'
];

const PERIOD_LABEL_FIELDS = Array.from(new Set([
    'period',
    'periode',
    'date',
    'year',
    'annee',
    ...CATEGORY_FIELDS
]));

const IGNORED_NUMERIC_FIELDS = new Set([
    'id',
    'app_id',
    'appId',
    ...METRIC_LABEL_FIELDS,
    ...PERIOD_LABEL_FIELDS,
    ...SERIES_FIELDS
]);

const isPresent = (value) => value !== undefined && value !== null && value !== '';

const asObject = (value) => value && typeof value === 'object' && !Array.isArray(value) ? value : null;

const getFiniteNumber = (value) => {
    const numericValue = parseNumber(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const getKnownValue = (row) => {
    const value = getFirstValue(row, VALUE_CANDIDATE_FIELDS);
    return getFiniteNumber(value);
};

const getFallbackNumericValue = (row) => {
    const entry = Object.entries(row || {}).find(([field, value]) => (
        !IGNORED_NUMERIC_FIELDS.has(field)
        && getFiniteNumber(value) !== null
    ));

    return entry ? getFiniteNumber(entry[1]) : null;
};

const getIndicatorValue = (row) => getKnownValue(row) ?? getFallbackNumericValue(row);

const getRowLabels = (row, fallbackLabel) => {
    const metricLabel = getFirstValue(row, METRIC_LABEL_FIELDS);
    const periodLabel = getFirstValue(row, PERIOD_LABEL_FIELDS);

    return {
        metricLabel: isPresent(metricLabel) ? String(metricLabel) : '',
        periodLabel: isPresent(periodLabel) ? String(periodLabel) : '',
        displayLabel: String(periodLabel || metricLabel || fallbackLabel || '')
    };
};

const normalizeRow = (row, index, fallbackLabel) => {
    const rowObject = asObject(row) || { value: row };
    const value = getIndicatorValue(rowObject);

    if (value === null) {
        return null;
    }

    return {
        ...getRowLabels(rowObject, fallbackLabel || (index === 0 ? 'Valeur courante' : 'Référence')),
        value,
        raw: rowObject
    };
};

const getRecordRows = (data) => {
    const dataObject = asObject(data);

    if (!dataObject) {
        return [];
    }

    const hasKnownValue = VALUE_CANDIDATE_FIELDS.some((field) => isPresent(dataObject[field]));
    if (hasKnownValue) {
        return [];
    }

    return Object.entries(dataObject)
        .filter(([field, value]) => !IGNORED_NUMERIC_FIELDS.has(field) && getFiniteNumber(value) !== null)
        .map(([label, value]) => ({ label, value }));
};

const dataToRows = (data) => {
    if (Array.isArray(data)) {
        return data;
    }

    const dataObject = asObject(data);
    if (!dataObject) {
        return [];
    }

    if (Array.isArray(dataObject.data)) {
        return dataObject.data;
    }

    if (Array.isArray(dataObject.values)) {
        return dataObject.values;
    }

    const explicitRowEntries = [
        ['current', dataObject.current],
        ['primary', dataObject.primary],
        ['main', dataObject.main],
        ['previous', dataObject.previous],
        ['previous', dataObject.previousValue],
        ['previous', dataObject.previous_value],
        ['reference', dataObject.reference],
        ['reference', dataObject.referenceValue],
        ['reference', dataObject.reference_value],
        ['secondary', dataObject.secondary]
    ]
        .filter(([, value]) => isPresent(value));

    const explicitRows = explicitRowEntries.map(([label, value]) => asObject(value) ? value : { label, value });

    if (explicitRows.length > 0) {
        const hasExplicitCurrent = explicitRowEntries.some(([label]) => ['current', 'primary', 'main'].includes(label));

        if (!hasExplicitCurrent && getIndicatorValue(dataObject) !== null) {
            return [dataObject, ...explicitRows];
        }

        return explicitRows;
    }

    const recordRows = getRecordRows(dataObject);
    if (recordRows.length > 1) {
        return recordRows;
    }

    return [dataObject];
};

export const normalizeIndicatorData = (data) => dataToRows(data)
    .map((row, index) => normalizeRow(row, index))
    .filter(Boolean)
    .slice(0, 2);

const getNumericOption = (options, fields) => {
    const rawValue = getFirstValue(options, fields);
    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : undefined;
};

const formatNumber = (value, options = {}) => {
    const fractionDigits = getNumericOption(options, ['precision', 'decimals', 'maximumFractionDigits']);
    const minimumFractionDigits = getNumericOption(options, ['minimumFractionDigits']);
    const maximumFractionDigits = fractionDigits ?? (Number.isInteger(value) ? 0 : 1);
    const prefix = options.valuePrefix ?? options.prefix ?? '';
    const suffix = options.valueSuffix ?? options.suffix ?? options.unit ?? '';

    const formattedValue = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: minimumFractionDigits ?? 0,
        maximumFractionDigits
    }).format(value);

    return `${prefix}${formattedValue}${suffix ? ` ${suffix}` : ''}`;
};

const formatPercent = (value, options = {}) => {
    const maximumFractionDigits = getNumericOption(options, ['percentPrecision', 'percentDecimals']) ?? 1;

    return new Intl.NumberFormat('fr-FR', {
        signDisplay: 'always',
        minimumFractionDigits: 0,
        maximumFractionDigits
    }).format(value) + ' %';
};

const formatDelta = (value, options = {}) => {
    const maximumFractionDigits = getNumericOption(options, ['deltaPrecision', 'deltaDecimals']) ?? 0;

    return new Intl.NumberFormat('fr-FR', {
        signDisplay: 'always',
        minimumFractionDigits: 0,
        maximumFractionDigits
    }).format(value);
};

const getTrend = (currentValue, previousValue) => {
    const delta = currentValue - previousValue;
    const percent = previousValue === 0 ? null : (delta / Math.abs(previousValue)) * 100;

    if (delta > 0) {
        return { delta, percent, direction: 'up', color: '#16a34a', Icon: TrendingUpIcon };
    }

    if (delta < 0) {
        return { delta, percent, direction: 'down', color: '#dc2626', Icon: TrendingDownIcon };
    }

    return { delta, percent, direction: 'flat', color: 'var(--text-secondary)', Icon: RemoveIcon };
};

const getComparisonLabel = (current, previous, options = {}) => {
    if (options.comparisonLabel) {
        return options.comparisonLabel;
    }

    if (previous?.displayLabel) {
        return `vs ${previous.displayLabel}`;
    }

    return current?.displayLabel ? `vs ${current.displayLabel}` : 'Comparatif';
};

const IndicatorChart = ({ data, title, scale = 100, titleFontSize, options = {} }) => {
    const indicators = useMemo(() => normalizeIndicatorData(data), [data]);
    const [current, previous] = indicators;
    const numericScale = Number(scale);
    const scaleFactor = Number.isFinite(numericScale) && numericScale > 0 ? numericScale / 100 : 1;
    const showComparison = options.showComparison !== false && Boolean(current && previous);

    if (!current) {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                minHeight: 140,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                textAlign: 'center',
                padding: 24
            }}>
                Aucune valeur à afficher
            </div>
        );
    }

    const resolvedTitle = title || options.title || current.metricLabel;
    const currentLabel = options.currentLabel || current.periodLabel || current.displayLabel;
    const trend = showComparison ? getTrend(current.value, previous.value) : null;
    const TrendIcon = trend?.Icon;

    return (
        <div style={{
            width: '100%',
            height: '100%',
            minHeight: 160,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 18,
            padding: '24px 54px 24px 24px',
            color: 'var(--text-color)'
        }}>
            {resolvedTitle && (
                <div style={{
                    fontSize: titleFontSize || '1.1rem',
                    lineHeight: 1.25,
                    fontWeight: 600,
                    color: 'var(--text-color)'
                }}>
                    {resolvedTitle}
                </div>
            )}

            <div>
                <div style={{
                    fontSize: `${3.8 * scaleFactor}rem`,
                    lineHeight: 1,
                    fontWeight: 700,
                    color: 'var(--accent)'
                }}>
                    {formatNumber(current.value, options)}
                </div>
                {currentLabel && (
                    <div style={{
                        marginTop: 8,
                        fontSize: `${0.95 * scaleFactor}rem`,
                        lineHeight: 1.3,
                        color: 'var(--text-secondary)'
                    }}>
                        {currentLabel}
                    </div>
                )}
            </div>

            {showComparison && (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '10px 16px',
                    paddingTop: 14,
                    borderTop: '1px solid var(--border-color)'
                }}>
                    <div style={{ minWidth: 130 }}>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: 1.2 }}>
                            {getComparisonLabel(current, previous, options)}
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-color)', lineHeight: 1.35 }}>
                            {formatNumber(previous.value, options)}
                        </div>
                    </div>

                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        color: trend.color,
                        fontSize: '1rem',
                        fontWeight: 700,
                        lineHeight: 1
                    }}>
                        {TrendIcon && <TrendIcon fontSize="small" />}
                        {trend.percent === null ? formatDelta(trend.delta, options) : formatPercent(trend.percent, options)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default IndicatorChart;
